// ─── src/Pages/AboutAndContact.js ──────────────────────────────────────────
// ABOUT US + CONTACT US pages, built to match the existing EthnicBeing
// design language (Playfair Display / Cormorant Garamond / DM Sans,
// #FAF8F4 background, #1A1612 ink, monochrome accents).

import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiMapPin, FiMail, FiPhone, FiClock, FiSend, FiCheckCircle,
} from "react-icons/fi";
import { miscAPI } from "./api";

/* ═══════════════════════════════════════════════════════════
   SHARED
═══════════════════════════════════════════════════════════ */
const PageHero = ({ eyebrow, title, sub }) => (
  <div style={{
    background: "#1A1612", color: "#FAF8F4",
    padding: "clamp(72px,12vw,120px) 24px clamp(48px,8vw,72px)",
    textAlign: "center",
  }}>
    <motion.p
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      style={{
        fontFamily: "'DM Sans',sans-serif", letterSpacing: 4, fontSize: 12,
        color: "#B8A98E", marginBottom: 16, textTransform: "uppercase",
      }}
    >
      {eyebrow}
    </motion.p>
    <motion.h1
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      style={{
        fontFamily: "'Playfair Display',serif", fontWeight: 700,
        fontSize: "clamp(32px,6vw,56px)", margin: 0,
      }}
    >
      {title}
    </motion.h1>
    {sub && (
      <motion.p
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic",
          fontSize: "clamp(16px,2vw,20px)", color: "#D8CDBB",
          maxWidth: 560, margin: "16px auto 0",
        }}
      >
        {sub}
      </motion.p>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   ABOUT US
═══════════════════════════════════════════════════════════ */
export const AboutUs = ({ navigate }) => {
  const values = [
    { title: "Made In India", desc: "Every piece is designed and manufactured in-house using premium high-gauge fabric sourced locally." },
    { title: "Rooted In Craft", desc: "Our prints draw from Indian textile traditions — block print, Warli, Ajrakh — reimagined for everyday wear." },
    { title: "Built To Last", desc: "We obsess over fabric weight, stitching and fit so pieces hold up wash after wash." },
    { title: "Fair & Transparent", desc: "Honest pricing, no inflated MRPs, and a return policy that actually works in your favour." },
  ];

  return (
    <div style={{ background: "#FAF8F4", minHeight: "100vh" }}>
      <PageHero
        eyebrow="Our Story"
        title="About Unleash Club"
        sub="Contemporary streetwear, woven with Indian heritage."
      />

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "clamp(48px,7vw,80px) 24px" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p style={{
            fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(19px,2.4vw,24px)",
            lineHeight: 1.7, color: "#1A1612", marginBottom: 24,
          }}>
           Unleash Club started with a simple idea: streetwear doesn't have to look
            like everyone else's. We take motifs and stories from India's textile
            heritage — Warli art, Ajrakh block prints, mirror work, rangoli patterns —
            and put them on pieces built for daily life, not display shelves.
          </p>
          <p style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: 16, lineHeight: 1.8,
            color: "#5C5346",
          }}>
            Every collection is designed in-house, cut from high-gauge premium
            cotton, and finished by hand-checked quality control before it ever
            reaches you. No middlemen, no inflated pricing — just clothing we're
            genuinely proud to put our name on.
          </p>
        </motion.div>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 32, marginTop: 64,
        }}>
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              style={{ borderTop: "2px solid #1A1612", paddingTop: 16 }}
            >
              <h3 style={{
                fontFamily: "'Playfair Display',serif", fontSize: 19, margin: "0 0 8px",
              }}>
                {v.title}
              </h3>
              <p style={{
                fontFamily: "'DM Sans',sans-serif", fontSize: 14.5, lineHeight: 1.6,
                color: "#7A6F5F", margin: 0,
              }}>
                {v.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 72 }}>
          <button
            onClick={() => navigate && navigate("products")}
            style={{
              fontFamily: "'DM Sans',sans-serif", letterSpacing: 1.5, fontWeight: 600,
              fontSize: 13, textTransform: "uppercase", background: "#1A1612",
              color: "#FAF8F4", border: "none", padding: "16px 40px",
              cursor: "pointer",
            }}
          >
            Shop The Collection
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CONTACT US
═══════════════════════════════════════════════════════════ */
export const ContactUs = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await miscAPI.contact(form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something went wrong. Please try again.");
    }
  };

  const inputStyle = {
    width: "100%", fontFamily: "'DM Sans',sans-serif", fontSize: 15,
    padding: "14px 16px", border: "1px solid #DCD3C4", background: "#fff",
    color: "#1A1612", outline: "none", marginBottom: 18,
  };

  const infoItems = [
    { icon: <FiMapPin />, label: "Studio", value: "Gurugram, Haryana, India" },
    { icon: <FiMail />, label: "Email", value: "support@unleashclub.com" },
    { icon: <FiPhone />, label: "Phone / WhatsApp", value: "+91 99999 99999" },
    { icon: <FiClock />, label: "Hours", value: "Mon–Sat, 10 AM – 7 PM IST" },
  ];

  return (
    <div style={{ background: "#FAF8F4", minHeight: "100vh" }}>
      <PageHero
        eyebrow="Get In Touch"
        title="Contact Us"
        sub="Questions about an order, a size, or a collab? We read every message."
      />

      <div style={{
        maxWidth: 1000, margin: "0 auto", padding: "clamp(48px,7vw,80px) 24px",
        display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 56,
      }}>
        {/* ── FORM ── */}
        <div>
          {status === "sent" ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              style={{
                border: "1px solid #1A1612", padding: "40px 32px",
                display: "flex", flexDirection: "column", alignItems: "flex-start",
                gap: 12,
              }}
            >
              <FiCheckCircle size={28} />
              <h3 style={{ fontFamily: "'Playfair Display',serif", margin: 0, fontSize: 22 }}>
                Message sent
              </h3>
              <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#5C5346", margin: 0 }}>
                Thanks for reaching out — we'll get back to you within 24–48 hours.
              </p>
              <button
                onClick={() => setStatus("idle")}
                style={{
                  marginTop: 8, background: "none", border: "1px solid #1A1612",
                  padding: "10px 20px", fontFamily: "'DM Sans',sans-serif",
                  fontSize: 13, letterSpacing: 1, cursor: "pointer",
                }}
              >
                SEND ANOTHER MESSAGE
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit}>
              <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, letterSpacing: 1, color: "#7A6F5F" }}>NAME</label>
              <input style={inputStyle} value={form.name} onChange={update("name")} placeholder="Your full name" />

              <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, letterSpacing: 1, color: "#7A6F5F" }}>EMAIL</label>
              <input style={inputStyle} type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" />

              <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, letterSpacing: 1, color: "#7A6F5F" }}>MESSAGE</label>
              <textarea
                style={{ ...inputStyle, minHeight: 140, resize: "vertical" }}
                value={form.message} onChange={update("message")}
                placeholder="How can we help?"
              />

              {error && (
                <p style={{ color: "#B3261E", fontFamily: "'DM Sans',sans-serif", fontSize: 13, marginTop: -8, marginBottom: 16 }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  fontFamily: "'DM Sans',sans-serif", letterSpacing: 1.5, fontWeight: 600,
                  fontSize: 13, textTransform: "uppercase", background: "#1A1612",
                  color: "#FAF8F4", border: "none", padding: "16px 36px",
                  cursor: status === "sending" ? "default" : "pointer",
                  opacity: status === "sending" ? 0.6 : 1,
                }}
              >
                <FiSend />
                {status === "sending" ? "SENDING…" : "SEND MESSAGE"}
              </button>
            </form>
          )}
        </div>

        {/* ── INFO ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {infoItems.map((item) => (
            <div key={item.label} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{
                width: 40, height: 40, display: "flex", alignItems: "center",
                justifyContent: "center", border: "1px solid #1A1612", flexShrink: 0,
              }}>
                {item.icon}
              </div>
              <div>
                <p style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: 1.5,
                  color: "#B8A98E", textTransform: "uppercase", margin: "0 0 4px",
                }}>
                  {item.label}
                </p>
                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, margin: 0 }}>
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};